import { useCallback, useEffect, useRef, useState } from 'react'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import Topbar from '@core/layout/topbar'
import StatusBar from '@core/layout/statusbar'
import PanelRail from '@core/layout/rail/PanelRail'
import Ribbon from '@core/layout/ribbon'
import { useRibbonShortcuts } from '@core/layout/ribbon/useRibbonShortcuts'
import MobileBar from '@core/layout/MobileBar'
import { useIsMobile } from '@core/useIsMobile'
import PasswordSetupBanner from '@core/auth/PasswordSetupBanner'
import VersionUpdateBanner from '@core/layout/VersionUpdateBanner'
import RibbonMovesNotice from '@core/layout/ribbon/RibbonMovesNotice'
import SessionRestoreBanner from '@core/session/SessionRestoreBanner'
import RecoveryPrompt from '@core/session/RecoveryPrompt'
import { useUnsavedWorkGuard } from '@core/session/useUnsavedWorkGuard'
import ViewerPage from '@viewer/ViewerPage'
import PopupsHost from '@viewer/popups'
import ModulePopup from '@core/layout/ModulePopup'
import ModuleFloatingPanel from '@core/layout/ModuleFloatingPanel'
import ModuleChrome, { ModuleCollapsedTab } from '@core/layout/ModuleChrome'
import { findModule } from '@modules/registry'
import JanelaPartilharHost from '@core/partilhas/JanelaPartilhar'
import AbrirSessaoPeloEndereco from '@core/sessoes/AbrirSessaoPeloEndereco'
import JanelaPedidosDeSessao from '@core/sessoes/JanelaPedidosDeSessao'
import { useAppStore } from '@core/store'
import { isDockMode } from '@core/store/workspaceLayoutSlice'

/**
 * Casca do workspace no modo cloud. A barra de topo sobrevive à troca de
 * módulo e a `ViewerPage` fica SEMPRE montada: remontá-la deita o
 * contexto WebGL abaixo e o modelo desaparece ao voltar.
 *
 * O módulo activo vem do `<Outlet />` da rota, mas o sítio onde ele é
 * desenhado é independente do URL: dock em baixo ou ao lado (com o 3D
 * vivo por cima), painel flutuante, janela do browser, ou página
 * inteira. A preferência é por módulo e fica guardada.
 *
 * Toda a árvore sai de UM return: em versões anteriores o modo "janela"
 * tinha um return próprio e a `ViewerPage` mudava de posição na árvore,
 * o que a remontava (e apagava o modelo) só por destacar um módulo.
 */
function WorkspaceLayout() {
  useRibbonShortcuts()
  useUnsavedWorkGuard()
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>()
  const location = useLocation()
  const layoutMode = useAppStore((s) => s.workspaceLayoutMode)
  const bcfHydrate = useAppStore((s) => s.bcfHydrate)
  useEffect(() => {
    void bcfHydrate()
  }, [bcfHydrate])
  // Zoom global do UI: aplicado em document.body.style.zoom para que
  // painéis, popups, menus e barras laterais escalem todos (o CSS zoom
  // é suportado em Chromium/WebKit e no Firefox 126+; noutros degrada
  // para "sem escala"). A tela do Three.js está dentro do mesmo zoom: a
  // matemática de picking usa rácios de getBoundingClientRect, por isso
  // as proporções mantêm-se; o framebuffer pode sair ligeiramente
  // reamostrado a escalas diferentes de 1, que é o preço de ter um só
  // botão para isto.
  const uiScale = useAppStore((s) => s.uiScale)
  useEffect(() => {
    const prev = document.body.style.zoom
    document.body.style.zoom = String(uiScale)
    return () => {
      document.body.style.zoom = prev
    }
  }, [uiScale])
  const setLayoutMode = useAppStore((s) => s.setWorkspaceLayoutMode)
  const setActiveModule = useAppStore((s) => s.setWorkspaceActiveModule)
  const moduleCollapsed = useAppStore((s) => s.workspaceModuleCollapsed)
  const setModuleCollapsed = useAppStore((s) => s.setWorkspaceModuleCollapsed)
  const sizeBottom = useAppStore((s) => s.workspaceModuleSizeBottom)
  const sizeSide = useAppStore((s) => s.workspaceModuleSizeSide)
  const setSizeBottom = useAppStore((s) => s.setWorkspaceModuleSizeBottom)
  const setSizeSide = useAppStore((s) => s.setWorkspaceModuleSizeSide)

  // Verdadeiro quando o URL aponta para /:workspaceSlug/ (com ou sem
  // barra final), ou seja, para a página do viewer e não para um módulo.
  const viewerPath = `/${workspaceSlug ?? ''}`
  const onViewerRoute =
    location.pathname === viewerPath || location.pathname === `${viewerPath}/`

  const onModuleRoute = !onViewerRoute && !!workspaceSlug

  // Sub-rotas próprias que não são módulos (administração, definir
  // palavra-passe) ignoram a preferência de layout e ocupam a página
  // toda: herdar um "janela" ou um dock deixado por uma sessão de BCF
  // abria a administração numa popup ou espremida, e parecia que a
  // administração não abria. O viewer por baixo fica escondido
  // (display:none), nunca desmontado.
  const builtInSubRoute =
    !!workspaceSlug &&
    (location.pathname === `/${workspaceSlug}/admin` ||
      location.pathname.startsWith(`/${workspaceSlug}/admin/`) ||
      location.pathname === `/${workspaceSlug}/account/password`)

  // Qual o módulo em que estamos (primeiro segmento a seguir ao slug do
  // workspace). É a chave da preferência de layout guardada.
  const currentModuleSlug =
    onModuleRoute && workspaceSlug && !builtInSubRoute
      ? (location.pathname
          .replace(`/${workspaceSlug}/`, '')
          .replace(`/${workspaceSlug}`, '')
          .split('/')[0] ?? null)
      : null

  useEffect(() => {
    setActiveModule(currentModuleSlug)
  }, [currentModuleSlug, setActiveModule])

  const moduleTitle = currentModuleSlug
    ? currentModuleSlug === 'partilhados'
      ? 'Partilhados comigo'
      : (findModule(currentModuleSlug)?.label ?? 'Módulo')
    : 'Módulo'

  useEffect(() => {
    // Clicar num separador é pedir para ver o módulo: honrar um
    // "encolhido" deixado para trás fazia parecer que o separador não
    // funcionava.
    if (currentModuleSlug && moduleCollapsed) setModuleCollapsed(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentModuleSlug])

  // Abrir, fechar ou redimensionar o dock muda a altura útil do viewer,
  // mas o renderer do ThatOpen só se remede a si próprio no evento
  // `resize` da JANELA: sem isto o canvas 3D ficava com o tamanho de
  // antes e passava por baixo do dock, e só voltava ao sítio quando o
  // utilizador mexia na janela. Um `resize` sintético é exactamente o
  // sinal que ele já sabe ouvir. Despachado já a seguir ao commit (os
  // efeitos correm com o DOM novo, e ler `clientHeight` obriga o
  // navegador a refazer o layout antes de responder) e não dentro de um
  // requestAnimationFrame: em separadores que não estão a compor frames
  // o rAF nunca corre e o canvas ficava com o tamanho errado.
  useEffect(() => {
    window.dispatchEvent(new Event('resize'))
  }, [
    layoutMode,
    isMobile,
    currentModuleSlug,
    builtInSubRoute,
    moduleCollapsed,
    sizeBottom,
    sizeSide,
  ])

  /** Fechar o painel = voltar à rota do viewer. O módulo mantém a
   *  preferência de layout para a próxima vez que for aberto. */
  const closeModule = useCallback(() => {
    if (workspaceSlug) navigate(`/${workspaceSlug}/`)
  }, [navigate, workspaceSlug])

  // Em telemóvel não há dock: o ecrã não dá para dois painéis e a barra
  // de topo já leva os separadores. Os módulos continuam a ocupar a
  // página, exactamente como antes.
  const effectiveLayoutMode = builtInSubRoute || isMobile ? 'full' : layoutMode

  const showModule = onModuleRoute
  // A barra de título do painel só faz sentido para módulos do registo:
  // a administração e o ecrã da palavra-passe não têm modo de layout.
  const showChrome = showModule && !builtInSubRoute && !isMobile
  const dockMode = isDockMode(effectiveLayoutMode)
  const detached =
    showChrome &&
    (effectiveLayoutMode === 'floating' || effectiveLayoutMode === 'window')

  // O módulo só entra no fluxo da página nos modos acoplados e no ecrã
  // inteiro. Destacado, o lugar dele fica vazio e o viewer ocupa tudo.
  const moduleInline = showModule && !detached
  const containerDir =
    effectiveLayoutMode === 'bottom' && moduleInline ? 'flex-col' : 'flex-row'
  const moduleSlotClass =
    effectiveLayoutMode === 'side'
      ? 'flex min-w-0 min-h-0 shrink-0 border-l border-[var(--color-border)]'
      : effectiveLayoutMode === 'bottom'
        ? 'flex min-w-0 min-h-0 shrink-0 border-t border-[var(--color-border)]'
        : 'flex min-w-0 min-h-0 flex-1'
  const moduleSlotStyle: React.CSSProperties =
    effectiveLayoutMode === 'side'
      ? { width: `${sizeSide}%` }
      : effectiveLayoutMode === 'bottom'
        ? { height: `${sizeBottom}%` }
        : {}

  // O viewer só se esconde quando o módulo ocupa mesmo a página toda.
  // Nos modos acoplados e nos destacados fica visível, que é o objectivo.
  const viewerVisible = !showModule || effectiveLayoutMode !== 'full'

  const renderCollapsedTab = moduleInline && dockMode && moduleCollapsed

  // O `<Outlet />` é desenhado UMA vez e entregue ao contentor do modo
  // em vigor. Passar o mesmo elemento evita que trocar de modo remonte o
  // módulo e perca o que ele tinha em estado local.
  const outlet = <Outlet />

  return (
    <div className="flex h-full flex-col bg-[var(--color-bg)] text-[var(--color-text)]">
      <Topbar />
      <JanelaPartilharHost />
      <AbrirSessaoPeloEndereco />
      <JanelaPedidosDeSessao />
      {!isMobile && <Ribbon visible={viewerVisible} />}
      {/* A nota do friso vem logo a seguir ao friso, que é o que ela
          explica. O aviso de versão fica acima de tudo o resto: é o
          mais urgente dos três. */}
      <VersionUpdateBanner />
      {!isMobile && <RibbonMovesNotice />}
      <PasswordSetupBanner />
      <SessionRestoreBanner />
      {/* A régua fica FORA do contentor com `containerDir`: esse pode
          ser coluna (dock em baixo) e a régua ficaria deitada.

          O `min-w-0` nos dois não é enfeite. Um filho de um contentor
          flex tem `min-width: auto`, ou seja, recusa-se a encolher
          abaixo do seu conteúdo. Sem ele, os 44px da régua eram
          somados em vez de descontados, e o painel de propriedades
          saía pela direita do ecrã fora. Medido: 3298px de largura
          onde só havia 956. */}
      <div className="flex min-h-0 min-w-0 flex-1">
        {!isMobile && onViewerRoute && <PanelRail />}
        <div className={`flex min-h-0 min-w-0 flex-1 ${containerDir}`}>
        <ViewerPage visible={viewerVisible} />
        {moduleInline &&
          (renderCollapsedTab ? (
            <ModuleCollapsedTab
              orientation={effectiveLayoutMode === 'side' ? 'side' : 'bottom'}
              title={moduleTitle}
              onExpand={() => setModuleCollapsed(false)}
            />
          ) : (
            <div
              className={`relative ${moduleSlotClass}`}
              style={moduleSlotStyle}
              data-testid="module-dock"
            >
              {dockMode && (
                <ResizeHandle
                  orientation={effectiveLayoutMode === 'side' ? 'side' : 'bottom'}
                  current={
                    effectiveLayoutMode === 'side' ? sizeSide : sizeBottom
                  }
                  onResize={(pct) => {
                    if (effectiveLayoutMode === 'side') setSizeSide(pct)
                    else setSizeBottom(pct)
                  }}
                />
              )}
              <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                {showChrome && (
                  <ModuleChrome
                    title={moduleTitle}
                    mode={effectiveLayoutMode}
                    setMode={setLayoutMode}
                    onClose={closeModule}
                    onCollapse={
                      dockMode ? () => setModuleCollapsed(true) : undefined
                    }
                  />
                )}
                <div className="min-h-0 min-w-0 flex-1 overflow-auto">
                  {outlet}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {detached && effectiveLayoutMode === 'floating' && (
        <ModuleFloatingPanel
          title={moduleTitle}
          mode={effectiveLayoutMode}
          setMode={setLayoutMode}
          onClose={closeModule}
        >
          {outlet}
        </ModuleFloatingPanel>
      )}
      {detached && effectiveLayoutMode === 'window' && (
        <ModulePopup>{outlet}</ModulePopup>
      )}
      {/* No telemóvel a rota do viewer troca a faixa de ferramentas pela
          barra de baixo. As rotas dos módulos ficam com a sua página
          inteira (navegada pelos separadores), por isso a barra só
          aparece no próprio viewer. */}
      {isMobile && onViewerRoute && <MobileBar />}
      {/* Pela mesma razao do MobileBar: no telemovel nao ha espaco, e
          nas paginas que nao sao o viewer os numeros do desenho nao
          querem dizer nada. */}
      {!isMobile && onViewerRoute && <StatusBar />}
      <PopupsHost />
      <RecoveryPrompt />
    </div>
  )
}

// Divisória de arrasto, na aresta interior do dock (topo no modo em
// baixo, esquerda no modo ao lado). Durante o arrasto lê-se o rectângulo
// do contentor flex para converter a posição do cursor em percentagem,
// para a divisória ficar colada ao cursor seja qual for o tamanho.
function ResizeHandle({
  orientation,
  current,
  onResize,
}: {
  orientation: 'side' | 'bottom'
  current: number
  onResize: (pct: number) => void
}) {
  const ref = useRef<HTMLDivElement | null>(null)
  const [active, setActive] = useState(false)
  useEffect(() => {
    if (!active) return
    const onMove = (e: PointerEvent) => {
      const handle = ref.current
      if (!handle) return
      const pane = handle.parentElement
      if (!pane) return
      const flexBox = pane.parentElement
      if (!flexBox) return
      const rect = flexBox.getBoundingClientRect()
      if (orientation === 'side') {
        onResize(((rect.right - e.clientX) / rect.width) * 100)
      } else {
        onResize(((rect.bottom - e.clientY) / rect.height) * 100)
      }
      e.preventDefault()
    }
    const onUp = () => setActive(false)
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [active, orientation, onResize])
  return (
    <div
      ref={ref}
      role="separator"
      data-testid="module-dock-resize"
      aria-orientation={orientation === 'side' ? 'vertical' : 'horizontal'}
      aria-valuenow={current}
      onPointerDown={(e) => {
        if (e.button !== 0) return
        setActive(true)
        e.preventDefault()
      }}
      title="Arrastar para redimensionar"
      className={`absolute z-30 ${
        orientation === 'side'
          ? 'left-0 top-0 h-full w-1.5 cursor-col-resize'
          : 'left-0 top-0 h-1.5 w-full cursor-row-resize'
      } ${active ? 'bg-[var(--color-accent)]/60' : 'bg-transparent hover:bg-[var(--color-accent)]/40'}`}
    />
  )
}

export default WorkspaceLayout
